const NAUTICAL_MILE_IN_METERS = 1852;
const EARTH_RADIUS_METERS = 6371000;

/**
 * Calculate new position based on bearing and distance
 */
export const newMarkPosition = (lat, lng, bearing, distance) => {
  const bearingRad = (bearing * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const distanceRad = distance / EARTH_RADIUS_METERS;

  const newLatRad = Math.asin(
    Math.sin(latRad) * Math.cos(distanceRad) +
    Math.cos(latRad) * Math.sin(distanceRad) * Math.cos(bearingRad)
  );

  const newLngRad = lngRad + Math.atan2(
    Math.sin(bearingRad) * Math.sin(distanceRad) * Math.cos(latRad),
    Math.cos(distanceRad) - Math.sin(latRad) * Math.sin(newLatRad)
  );

  return {
    lat: (newLatRad * 180) / Math.PI,
    lng: (newLngRad * 180) / Math.PI
  };
};

/**
 * Calculate distance between two points in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const deltaLatRad = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLngRad = ((lng2 - lng1) * Math.PI) / 180;

  const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) *
    Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return EARTH_RADIUS_METERS * c;
};

/**
 * Calculate distance from a point to the closest point on a line segment
 * For start/finish lines, this gives more accurate distance than point-to-point
 * Uses perpendicular projection to find the closest point on the line segment
 */
export const calculateDistanceToLine = (pointLat, pointLng, line1Lat, line1Lng, line2Lat, line2Lng) => {
  // Convert to radians
  const lat1Rad = (line1Lat * Math.PI) / 180;
  const lng1Rad = (line1Lng * Math.PI) / 180;
  const lat2Rad = (line2Lat * Math.PI) / 180;
  const lng2Rad = (line2Lng * Math.PI) / 180;
  const ptLatRad = (pointLat * Math.PI) / 180;
  const ptLngRad = (pointLng * Math.PI) / 180;
  
  // Convert to Cartesian coordinates (approximation for small distances)
  const x1 = lng1Rad * Math.cos(lat1Rad);
  const y1 = lat1Rad;
  const x2 = lng2Rad * Math.cos(lat2Rad);
  const y2 = lat2Rad;
  const xp = ptLngRad * Math.cos(ptLatRad);
  const yp = ptLatRad;
  
  // Line segment vector
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  
  if (lengthSq === 0) {
    // Line endpoints are the same, just return distance to that point
    return calculateDistance(pointLat, pointLng, line1Lat, line1Lng);
  }
  
  // Project point onto line segment (parametric parameter t)
  // t = 0 means closest to line1, t = 1 means closest to line2
  let t = ((xp - x1) * dx + (yp - y1) * dy) / lengthSq;
  
  // Clamp t to [0, 1] to stay within line segment
  t = Math.max(0, Math.min(1, t));
  
  // Find closest point on line segment
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  
  // Convert back to lat/lng
  const closestLat = closestY * (180 / Math.PI);
  const closestLng = closestX / Math.cos(closestY) * (180 / Math.PI);
  
  // Return distance to closest point
  return calculateDistance(pointLat, pointLng, closestLat, closestLng);
};

/**
 * Calculate heading between two points
 */
export const calculateHeading = (lat1, lng1, lat2, lng2) => {
  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const deltaLngRad = ((lng2 - lng1) * Math.PI) / 180;

  const y = Math.sin(deltaLngRad) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLngRad);

  const headingRad = Math.atan2(y, x);
  return ((headingRad * 180) / Math.PI + 360) % 360;
};

/**
 * Calculate bearing from point 1 to point 2 in degrees (0-360)
 * Alias for calculateHeading for semantic clarity in VMC/VMG calculations
 * 0° = North, 90° = East, 180° = South, 270° = West
 */
export const calculateBearing = calculateHeading;

/**
 * Calculate VMC (Velocity Made good on Course)
 * Returns speed component towards target mark
 * 
 * @param {number} speed - Boat speed in m/s (SOG - Speed Over Ground)
 * @param {number} heading - Boat compass heading in degrees (0-360)
 * @param {number} bearing - Bearing to target mark in degrees (0-360)
 * @returns {number|null} VMC in m/s (positive when sailing towards mark, negative when sailing away)
 * 
 * @example
 * // Boat sailing directly towards mark
 * calculateVMC(5.0, 90, 90) // => 5.0 m/s
 * 
 * // Boat sailing at 45° angle to mark
 * calculateVMC(5.0, 45, 90) // => ~3.54 m/s
 * 
 * // Boat sailing perpendicular to mark (no progress)
 * calculateVMC(5.0, 0, 90) // => ~0 m/s
 * 
 * // Boat sailing away from mark
 * calculateVMC(5.0, 270, 90) // => -5.0 m/s
 */
export const calculateVMC = (speed, heading, bearing) => {
  if (speed === null || speed === undefined ||
      heading === null || heading === undefined ||
      bearing === null || bearing === undefined) {
    return null;
  }
  
  // Calculate angle difference between heading and bearing
  let angleDiff = Math.abs(heading - bearing);
  
  // Normalize to 0-180 (we only care about the acute angle)
  if (angleDiff > 180) {
    angleDiff = 360 - angleDiff;
  }
  
  // Convert to radians
  const angleDiffRad = (angleDiff * Math.PI) / 180;
  
  // VMC = speed * cos(angle)
  // Positive when heading towards mark, negative when heading away
  const vmc = speed * Math.cos(angleDiffRad);
  
  // If angle > 90°, boat is sailing away from mark (VMC should be negative)
  if (angleDiff > 90) {
    return -Math.abs(vmc);
  }
  
  return vmc;
};

/**
 * Calculate VMG (Velocity Made Good towards Wind)
 * Returns speed component towards wind direction
 * 
 * @param {number} speed - Boat speed in m/s (SOG - Speed Over Ground)
 * @param {number} heading - Boat compass heading in degrees (0-360)
 * @param {number} windDirection - Wind direction in degrees (0-360, direction wind is coming FROM)
 * @returns {number|null} VMG in m/s (positive when sailing towards wind, negative when sailing downwind)
 * 
 * @example
 * // Boat sailing directly upwind
 * calculateVMG(5.0, 0, 0) // => 5.0 m/s
 * 
 * // Boat tacking at 45° to wind
 * calculateVMG(5.0, 45, 0) // => ~3.54 m/s
 * 
 * // Boat sailing perpendicular to wind
 * calculateVMG(5.0, 90, 0) // => ~0 m/s
 * 
 * // Boat sailing downwind
 * calculateVMG(5.0, 180, 0) // => -5.0 m/s
 */
export const calculateVMG = (speed, heading, windDirection) => {
  if (speed === null || speed === undefined ||
      heading === null || heading === undefined ||
      windDirection === null || windDirection === undefined) {
    return null;
  }
  
  // Calculate angle difference between heading and wind direction
  let angleDiff = Math.abs(heading - windDirection);
  
  // Normalize to 0-180
  if (angleDiff > 180) {
    angleDiff = 360 - angleDiff;
  }
  
  // Convert to radians
  const angleDiffRad = (angleDiff * Math.PI) / 180;
  
  // VMG = speed * cos(angle)
  // Positive when heading towards wind (upwind), negative when heading away (downwind)
  const vmg = speed * Math.cos(angleDiffRad);
  
  // If angle > 90°, boat is sailing downwind (VMG should be negative)
  if (angleDiff > 90) {
    return -Math.abs(vmg);
  }
  
  return vmg;
};

/**
 * Calculate triangle dimensions for course layout
 */
export const calculateTriangle = (a, windwardBeat, angleB) => {
  let length = windwardBeat * 2;
  let b, angleA;
  let newBeat = windwardBeat;

  while (true) {
    b = Math.sqrt(a ** 2 + newBeat ** 2 - 2 * a * newBeat * Math.cos(angleB * Math.PI / 180));
    angleA = Math.acos((b ** 2 + newBeat ** 2 - a ** 2) / (2 * b * newBeat)) * 180 / Math.PI;

    if (newBeat + b > length) {
      newBeat -= 1;
    } else {
      break;
    }
  }

  return {
    leewardLeg: b,
    windwardLeg: newBeat,
    angleA: angleA
  };
};

/**
 * Calculate beat length for course
 */
export const calculateBeatLength = (
  windDirection, 
  distance, 
  leewardDistance, 
  numWindwardBeats, 
  offset, 
  offsetAngle, 
  offsetDistance, 
  finishType, 
  finishDistance
) => {
  if (!offset) {
    // If we do not use offset assume angle the same as wind direction
    offsetAngle = windDirection;
    offsetDistance = 0;
  }

  if (['Start finish together'].includes(finishType)) {
    finishDistance = 0;
  }
  
  let initialBeatLength = (distance - leewardDistance - offsetDistance - finishDistance) / (numWindwardBeats * 2);

  return calculateTriangle(offsetDistance, initialBeatLength, windDirection - offsetAngle);
};